import React, { useCallback, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  DataTable,
  DataTableCellVariants,
  DataTableColumnAlignments,
  DataTableRowData,
  RowsSkeletonLoader,
  Switch,
  toast,
} from '@node-space/ui-components'
import { DurationInMilliseconds } from 'constants/General'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { useCreateNotificationDestinationsMutation } from 'hooks/mutations/useCreateNotificationDestinationsMutation'
import { useDeleteNotificationDestinationMutation } from 'hooks/mutations/useDeleteNotificationDestinationMutation'
import { useNotificationDestinationsQuery } from 'hooks/queries/WebHooks/useNotificationDestinationsQuery'
import { reactQueryName } from 'reactQueryKeys/reactQueryKeys'

const APPROVAL_REQUIRED_EVENT_CODE = 'bvnk:approvals:payout:approval-requested'

export const CommunicationPreferencesTable = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { profile } = useProfileContext()
  const userEmail = profile?.emailAddress

  const { data: { content: destinations } = {}, isFetching } = useNotificationDestinationsQuery(
    '',
    50
  )

  const approvalDestination = useMemo(
    () =>
      destinations?.find(
        d =>
          !!userEmail &&
          d.type === 'EMAIL' &&
          d.target === userEmail &&
          d.eventCodes?.includes(APPROVAL_REQUIRED_EVENT_CODE)
      ),
    [destinations, userEmail]
  )

  const isApprovalEnabled = !!approvalDestination

  const { mutate: createDestination, isPending: isCreating } =
    useCreateNotificationDestinationsMutation()
  const { mutate: deleteDestination, isPending: isDeleting } =
    useDeleteNotificationDestinationMutation()

  const isToggling = isCreating || isDeleting

  const handleToggle = useCallback(() => {
    if (isToggling) return

    if (isApprovalEnabled) {
      if (!approvalDestination) return
      deleteDestination(
        { destinationId: approvalDestination.id },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [reactQueryName.DESTINATIONS] })
            toast({
              header: t('communicationPreferences.unsubscribed'),
              variant: 'success',
              duration: DurationInMilliseconds.TEN_SECONDS,
            })
          },
          onError: () => {
            toast({
              header: t('communicationPreferences.errorUnsubscribing'),
              variant: 'error',
              duration: DurationInMilliseconds.TEN_SECONDS,
            })
          },
        }
      )
    } else {
      if (!userEmail) return
      createDestination(
        {
          target: userEmail,
          description: t('communicationPreferences.notifications.approvalRequired'),
          type: 'EMAIL',
          events: [APPROVAL_REQUIRED_EVENT_CODE],
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [reactQueryName.DESTINATIONS] })
            toast({
              header: t('communicationPreferences.subscribed'),
              variant: 'success',
              duration: DurationInMilliseconds.TEN_SECONDS,
            })
          },
          onError: () => {
            toast({
              header: t('communicationPreferences.errorSubscribing'),
              variant: 'error',
              duration: DurationInMilliseconds.TEN_SECONDS,
            })
          },
        }
      )
    }
  }, [
    isApprovalEnabled,
    isToggling,
    approvalDestination,
    userEmail,
    queryClient,
    t,
    createDestination,
    deleteDestination,
  ])

  const columns = [
    {
      key: 'notification',
      label: t('communicationPreferences.columns.notification'),
      variant: DataTableCellVariants.TEXT,
    },
    {
      key: 'email',
      label: t('communicationPreferences.columns.email'),
      variant: DataTableCellVariants.ACTION,
      align: DataTableColumnAlignments.RIGHT,
    },
  ]

  const tableData = useMemo(
    (): DataTableRowData[] => [
      {
        id: 'approval-required',
        cells: [
          {
            key: 'notification',
            variant: DataTableCellVariants.TEXT,
            Text: t('communicationPreferences.notifications.approvalRequired'),
            BottomText: t('communicationPreferences.notifications.approvalRequiredDescription'),
          },
          {
            key: 'email',
            variant: DataTableCellVariants.ACTION,
            align: DataTableColumnAlignments.RIGHT,
            Actions: [
              <Switch
                key="approval-required-email"
                value={isApprovalEnabled}
                onChange={handleToggle}
                disabled={isToggling || isFetching}
              />,
            ],
          },
        ],
      },
    ],
    [isApprovalEnabled, isToggling, isFetching, handleToggle]
  )

  if (isFetching) {
    return <RowsSkeletonLoader rows={3} />
  }

  return <DataTable headers={columns} data={tableData} />
}
